import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Grid } from "semantic-ui-react";
import { cartTotalPriceSelector } from "../../../utils/selectors";
import PrimaryButton from "../../../components/Button/PrimaryButton";
import { Heading3, Heading5 } from "../../../components/value-cssmodules/TextComponent";
import BasicInput from "../../../components/Inputs/BasicInput";
import LightButton from "../../../components/Button/LightButton";
import { toast } from "react-toastify";
import { formatEur } from "../../../utils/patternPricing";
import { applyPromo, clearPromo } from "../../../actions/promoActions";
import styles from "./PaymentActions.module.scss";

const PaymentActions = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const intl = useIntl();
  const totalPrice = useSelector((state: any) => cartTotalPriceSelector(state));
  const cartItems = useSelector((state: any) => state.cart);
  const promo = useSelector((state: any) => state.promo);

  const [input, setInput] = React.useState("");

  const applyDiscount = () => {
    const discountCode = input.trim();
    if (!discountCode) return;
    dispatch(applyPromo(discountCode, cartItems));
    toast.info(intl.formatMessage({ id: "cart.toast.applyingPromo", defaultMessage: "Applying promo..." }));
  };

  return (
    <Grid className={styles.self}>
      {/* ── HEADER ── */}
      <Grid.Row centered>
        <Grid.Column>
          <Heading3 centered>
            <FormattedMessage id="cart.summary" defaultMessage="Summary" />
          </Heading3>
        </Grid.Column>
      </Grid.Row>

      {/* ── PROMO INPUT ── */}
      <Grid.Row>
        <Grid.Column>
          <Heading5>
            <FormattedMessage id="cart.promoCode.heading" defaultMessage="Have a promo code?" />
          </Heading5>

          <div className={styles.inputRow}>
            <BasicInput
              placeholder={intl.formatMessage({ id: "cart.promoCode.placeholder", defaultMessage: "Enter code" })}
              className={styles.input}
              value={input}
              onChange={(e) => setInput(e.target.value.toUpperCase())}
            />
            <PrimaryButton className={styles.applyButton} onClick={applyDiscount}>
              <FormattedMessage id="cart.apply" defaultMessage="Apply" />
            </PrimaryButton>
          </div>
        </Grid.Column>
      </Grid.Row>

      {/* ── PROMO SUCCESS BOX ── */}
      {promo?.applied && (
        <Grid.Row>
          <Grid.Column>
            <div className={styles.promoBox}>
              <div className={styles.promoHeader}>
                <span>
                  {intl.formatMessage(
                    { id: "cart.promoCode.applied", defaultMessage: "✓ {code} applied" },
                    { code: promo.code }
                  )}
                </span>
                <span>✓</span>
              </div>

              <div className={styles.promoSub}>
                {promo.type === "percentage"
                  ? intl.formatMessage(
                      { id: "cart.promoCode.discountPercentage", defaultMessage: "{value}% off" },
                      { value: promo.discountValue }
                    )
                  : intl.formatMessage(
                      { id: "cart.promoCode.discountAmount", defaultMessage: "{value} off" },
                      { value: formatEur(promo.discountValue) }
                    )}
              </div>

              <div className={styles.promoMeta}>
                {intl.formatMessage(
                  {
                    id: "cart.promoCode.appliesTo",
                    defaultMessage: "Applies to {count, plural, one {# item} other {# items}}",
                  },
                  { count: promo.validItems }
                )}
                {promo.creatorBreakdown?.length === 1
                  ? ` ${intl.formatMessage(
                      { id: "cart.promoCode.fromCreator", defaultMessage: "from {creator}" },
                      { creator: promo.creatorBreakdown[0].creatorName }
                    )}`
                  : ""}
              </div>

              <LightButton onClick={() => dispatch(clearPromo())}>
                {intl.formatMessage({ id: "cart.remove", defaultMessage: "Remove" })}
              </LightButton>
            </div>
          </Grid.Column>
        </Grid.Row>
      )}

      {/* ── SUBTOTAL ── */}
      <Grid.Row>
        <Grid.Column className={styles.total}>
          <Heading5>
            {intl.formatMessage(
              { id: "cart.subtotal", defaultMessage: "Subtotal ({count, plural, one {# item} other {# items}})" },
              { count: cartItems?.length ?? 0 }
            )}
          </Heading5>
          <Heading3>{formatEur(totalPrice)}</Heading3>
        </Grid.Column>
      </Grid.Row>

      {/* ── DISCOUNT ── */}
      {promo.applied && (
        <Grid.Row>
          <Grid.Column className={styles.total}>
            <Heading5 className={styles.discount}>
              {intl.formatMessage(
                { id: "cart.discount", defaultMessage: "Discount ({code})" },
                { code: promo.code }
              )}
            </Heading5>
            <Heading3 className={styles.discount}>
              -{formatEur(promo.discount)}
            </Heading3>
          </Grid.Column>
        </Grid.Row>
      )}

      {/* ── CREATOR BREAKDOWN ── */}
      {promo.applied &&
        promo.creatorBreakdown?.map((group: any) => (
          <Grid.Row key={group.creator}>
            <Grid.Column className={styles.total}>
              <Heading5>
                {intl.formatMessage(
                  {
                    id: "cart.creatorBreakdown.title",
                    defaultMessage: "{count, plural, one {# item} other {# items}} from {creator}",
                  },
                  { count: group.items, creator: group.creatorName }
                )}
              </Heading5>

              <div>
                <Heading3>{formatEur(group.total)}</Heading3>
                {group.discount > 0 && (
                  <div className={styles.discountSmall}>
                    -{formatEur(group.discount)}
                  </div>
                )}
              </div>
            </Grid.Column>
          </Grid.Row>
        ))}

      {/* ── DIVIDER ── */}
      <Grid.Row>
        <Grid.Column>
          <hr className={styles.divider} />
        </Grid.Column>
      </Grid.Row>

      {/* ── TOTAL ── */}
      <Grid.Row>
        <Grid.Column className={styles.total}>
          <Heading5>
            <FormattedMessage id="cart.total" defaultMessage="Total" />
          </Heading5>
          <Heading3>{formatEur(promo.applied ? promo.total : totalPrice)}</Heading3>
        </Grid.Column>
      </Grid.Row>

      {/* ── PAY BUTTON ── */}
      <Grid.Row>
        <Grid.Column width={16}>
          <PrimaryButton onClick={() => navigate("/order?paymentMethod=Stripe")}>
            <FormattedMessage id="cart.pay" defaultMessage="Pay" />
          </PrimaryButton>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export default PaymentActions;